/**
 * Global Imports
*/

import * as Pixi from 'pixi.js';

/**
 * System Imports
*/

import { ChatMessage, ChatAddEventHandler } from '@system/Chat';
import { RandomFrom, RandomInt } from '@system/Utility';

/**
 * Relative Imports
*/

import { Pokemon, Sprite, Version } from './types';

/**
 * Pixi Settings
*/

Pixi.settings.RESOLUTION = window.devicePixelRatio || 1;
Pixi.settings.MIPMAP_TEXTURES = Pixi.MIPMAP_MODES.OFF;
Pixi.settings.SCALE_MODE = Pixi.SCALE_MODES.NEAREST;

/**
 * Locals
*/

let _uid = 0;
let _mask: Pixi.Graphics;
let _container: Pixi.Container;
let _gfx: Pixi.Application = null;
let _names: Record<string, Record<string, Record<string, string>>>;
let _active: Array<Sprite> = [];

/**
 * Private Functions
*/

let prev = 0;

/**
 * @return {void}
 */
function _update(time: number): void
{
    const delta = (time - prev);
    const step = (delta * 0.062111801);
    const remove: Array<[number,number]> = [];

    let i = 0;

    for (const item of _active) {
        if (item.ttl === 1) {
            if (item.sprite.alpha > 0) {
                item.sprite.alpha -= 0.025;
            } else {
                remove.push([i, item.id]);
            }
        } else {
            item.ttl--;

            if (item.sprite.alpha < 1) {
                item.sprite.alpha += 0.025;
                if (item.sprite.alpha > 1) {
                    item.sprite.alpha = 1;
                }
            }
        }

        if (item.scale > 2) {
            item.scale -= 0.25;
            if (item.scale < 2) {
                item.scale = 2;
            }
            item.sprite.scale.x = item.scale;
            item.sprite.scale.y = item.scale;
        }

        if (item.direction.x === 1) {
            if ((item.sprite.x + (item.sprite.width/2) + (step * item.speed.x)) >= window.innerWidth) {
                item.sprite.x = (window.innerWidth - (item.sprite.width / 2));
                item.direction.x = 0;
            } else {
                item.sprite.x += (step * item.speed.x);
            }
        } else {
            if ((item.sprite.x - (item.sprite.width/2) - (step * item.speed.x)) < 0) {
                item.sprite.x = (item.sprite.width/2);
                item.direction.x = 1;
            } else {
                item.sprite.x -= (step * item.speed.x);
            }
        }

        if (item.direction.y === 1) {
            if ((item.sprite.y + (item.sprite.height/2) + (step * item.speed.y)) >= window.innerHeight) {
                item.sprite.y = (window.innerHeight - (item.sprite.height/2));
                item.direction.y = 0;
            } else {
                item.sprite.y += (step * item.speed.y);
            }
        } else {
            if ((item.sprite.y - (item.sprite.height/2) - (step * item.speed.y)) < 0) {
                item.sprite.y = (item.sprite.height/2);
                item.direction.y = 1;
            } else {
                item.sprite.y -= (step * item.speed.y);
            }
        }

        i++;
    }

    const newActive = [];

    for (let i = 0; i < _active.length; i++) {
        let keep = true;

        for (let j = 0; j < remove.length; j++) {
            if (_active[i].id === remove[j][1]) {
                keep = false;
                _container.removeChild(_active[i].sprite);
                break;
            }
        }

        if (keep) {
            newActive.push(_active[i]);
        }
    }

    _active = newActive;
    prev = time;
    requestAnimationFrame(_update);
}

/**
 * @return {Promise<Pixi.Texture>}
 */
export function _createTexture(uri: string): Promise<Pixi.Texture>
{
    return new Promise(async (resolve) => {
        const storage = localStorage.getItem(uri);

        if (storage) {
            return resolve(new Pixi.Texture(Pixi.BaseTexture.from(storage)));
        }

        const reader = new FileReader;
        const request = await fetch(uri);
        const blob = await request.blob();

        reader.onloadend = (): void => {
            localStorage.setItem(uri, reader.result as string);
            resolve(new Pixi.Texture(Pixi.BaseTexture.from(reader.result as string)));
        };

        reader.readAsDataURL(blob);
    });
}

/**
 * @return {Promise<void>}
 */
async function _showPokemon(pokemon: Pokemon): Promise<void>
{
    const version = RandomFrom(pokemon.versions.filter((item: Version): boolean => item !== 'crystal'));
    const sprite = new Pixi.Sprite(
        await _createTexture(`/images/pokemon/${pokemon.number}-${pokemon.key}/${version}/${pokemon.key}-front${version === 'red-blue' || version === 'yellow' ? '' : '-normal'}.png`)
    );

    sprite.anchor.x = 0.5;
    sprite.anchor.y = 0.5;
    sprite.x = window.innerWidth / 2;
    sprite.y = window.innerHeight / 2;
    sprite.scale.x = 2;
    sprite.scale.y = 2;
    sprite.alpha = 0;

    _container.addChild(sprite);
    _active.push({
        sprite,
        id: _uid++,
        ttl: Math.round((Math.random() * 800) + 600),
        scale: 2,
        speed: { x: (Math.random() * 2) + 0.25, y: (Math.random() * 2) + 0.25 },
        direction: { x: Math.random() > 0.5 ? 1 : 0, y: Math.random() > 0.5 ? 1 : 0 },
    });
}

// 344x490

/**
 * @return {Promise<void>}
 */
async function _fetchAndShowPokemon(name: string): Promise<void>
{
    const lower = name.toLowerCase();
    const key = `/json/pokemon/${lower}.json`;
    const storage = localStorage.getItem(key);

    if (storage) {
        return _showPokemon(JSON.parse(storage));
    }

    const response = await fetch(key);
    const data = await response.json();

    localStorage.setItem(key, JSON.stringify(data));

    _showPokemon(data);
}

/**
 * @return {void}
 */
function _isThreeLetterName(name: string): boolean
{
    switch (name[0]) {
        case 'm':
        case 'M':
            switch (name[1]) {
                case 'e': return name[2] === 'w';
                case 'u': return name[2] === 'k';
                default:  return false;
            }
    }

    return false;
}

/**
 * @return {void}
 */
function _isFourLetterName(name: string): boolean
{
    switch (name[0]) {
        case 'a':
        case 'A':
            switch (name[1]) {
                case 'b': return name[2] === 'r' && name[3] === 'a';
                case 'r': return name[2] === 'o' && name[3] === 'n';
                case 'x': return name[2] === 'e' && name[3] === 'w';
                default:  return false;
            }
        case 'h':
        case 'H':
            return name[1] === 'o' && name[2] === 'o' && name[3] === 'h';
        case 'j':
        case 'J':
            return name[1] === 'y' && name[2] === 'n' && name[3] === 'x';
        case 'n':
        case 'N':
            return name[1] === 'a' && name[2] === 't' && name[3] === 'u';
        case 'o':
        case 'O':
            return name[1] === 'n' && name[2] === 'i' && name[3] === 'x';
        case 's':
        case 'S':
            switch (name[1]) {
                case 'a': return name[2] === 'w' && name[3] === 'k';
                case 'e': return name[2] === 'e' && name[3] === 'l';
                default:  return false;
            }
        case 'u':
        case 'U':
            return name[1] === 'x' && name[2] === 'i' && name[3] === 'e';
        case 'x':
        case 'X':
            return name[1] === 'a' && name[2] === 't' && name[3] === 'u';
    }

    return false;
}

/**
 * @return {void}
 */
function _isFiveLetterName(name: string): boolean
{
    switch (name[0]) {
        case 'a':
        case 'A':
            switch (name[1]) {
                case 'b': return name[2] === 's' && name[3] === 'o' && name[4] === 'l';
                case 'i': return name[2] === 'p' && name[3] === 'o' && name[4] === 'm';
                case 'r': return name[2] === 'b' && name[3] === 'o' && name[4] === 'k';
                case 'z': return name[2] === 'e' && name[3] === 'l' && name[4] === 'f';
                default:  return false;
            }
        case 'b':
        case 'B':
            switch (name[2]) {
                case 'g': return name[1] === 'a' && name[3] === 'o' && name[4] === 'n';
                case 'd': return name[1] === 'u' && name[3] === 'e' && name[4] === 'w';
                case 'r': return name[1] === 'u' && name[3] === 'm' && name[4] === 'y';
                default:  return false;
            }
        case 'd':
        case 'D':
            switch (name[1]) {
                case 'e': return name[2] === 'i' && name[3] === 'n' && name[4] === 'o';
                case 'i': return name[2] === 't' && name[3] === 't' && name[4] === 'o';
                case 'o': return name[2] === 'd' && name[3] === 'u' && name[4] === 'o';
                default:  return false;
            }
        case 'e':
        case 'E':
            switch (name[1]) {
                case 'e': return name[2] === 'v' && name[3] === 'e' && name[4] === 'e';
                case 'k': return name[2] === 'a' && name[3] === 'n' && name[4] === 's';
                case 'n': return name[2] === 't' && name[3] === 'e' && name[4] === 'i';
                default:  return false;
            }
        case 'g':
        case 'G':
            switch (name[1]) {
                case 'i': return name[2] === 'b' && name[3] === 'l' && name[4] === 'e';
                case 'l': return name[2] === 'o' && name[3] === 'o' && name[4] === 'm';
                case 'o': return name[2] === 'l' && name[3] === 'e' && name[4] === 'm';
                default:  return false;
            }
        case 'h':
        case 'H':
            switch (name[1]) {
                case 'o': return name[2] === '-' && name[3] === 'o' && name[4] === 'h';
                case 'y': return name[2] === 'p' && name[3] === 'n' && name[4] === 'o';
                default:  return false;
            }
        case 'k':
        case 'K':
            switch (name[2]) {
                case 'a': return name[1] === 'l' && name[3] === 'n' && name[4] === 'g';
                case 'i': return name[1] === 'l' && name[3] === 'n' && name[4] === 'k';
                default:  return false;
            }
        case 'l':
        case 'L':
            switch (name[2]) {
                case 'g': return name[1] === 'u' && name[3] === 'i' && name[4] === 'a';
                case 't': return name[1] === 'o' && name[3] === 'a' && name[4] === 'd';
                case 'x': return name[1] === 'u' && name[3] === 'i' && name[4] === 'o';
                default:  return false;
            }
        case 'm':
        case 'M':
            switch (name[1]) {
                case 'a': return name[2] === 'g' && name[3] === 'b' && name[4] === 'y';
                case 'i': return name[2] === 'n' && name[3] === 'u' && name[4] === 'n';
                case 'u': return name[2] === 'n' && name[3] === 'n' && name[4] === 'a';
                default:  return false;
            }
        case 'n':
        case 'N':
            return name[1] === 'u' && name[2] === 'm' && name[3] === 'e' && name[4] === 'l';
        case 'p':
        case 'P':
            switch (name[1]) {
                case 'a': return name[2] === 'r' && name[3] === 'a' && name[4] === 's';
                case 'i': return name[2] === 'c' && name[3] === 'h' && name[4] === 'u';
                default:  return false;
            }
        case 'r':
        case 'R':
            switch (name[1]) {
                case 'a': return name[2] === 'l' && name[3] === 't' && name[4] === 's';
                case 'i': return name[2] === 'o' && name[3] === 'l' && name[4] === 'u';
                case 'o': return name[2] === 't' && name[3] === 'o' && name[4] === 'm';
                default:  return false;
            }
        case 's':
        case 'S':
            switch (name[1]) {
                case 'h': return name[2] === 'i' && name[3] === 'n' && name[4] === 'x';
                case 'n': return name[2] === 'i' && name[3] === 'v' && name[4] === 'y';
                default:  return false;
            }
        case 't':
        case 'T':
            switch (name[1]) {
                case 'e': return name[2] === 'p' && name[3] === 'i' && name[4] === 'g';
                case 'h': return name[2] === 'r' && name[3] === 'o' && name[4] === 'h';
                default:  return false;
            }
        case 'u':
        case 'U':
            return name[1] === 'n' && name[2] === 'o' && name[3] === 'w' && name[4] === 'n';
        case 'y':
        case 'Y':
            return name[1] === 'a' && name[2] === 'n' && name[3] === 'm' && name[4] === 'a';
        case 'z':
        case 'Z':
            switch (name[1]) {
                case 'u': return name[2] === 'b' && name[3] === 'a' && name[4] === 't';
                case 'o': return name[2] === 'r' && name[3] === 'u' && name[4] === 'a';
                default:  return false;
            }
    }

    return false;
}

/**
 * @return {void}
 */
function _isLongName(length: number, name: string): boolean
{
    const key = name[0].toLowerCase();
    const valids = _names[`${length}`][key];

    if (!valids) {
        return false;
    }

    return name.toLowerCase() in valids;
}

/**
 * @return {void}
 */
async function _checkWordForPokemon(word: string): Promise<void>
{
    switch (word.length) {
    case 3:
        if (!_isThreeLetterName(word)) {
            return;
        }
        break;
    case 4:
        if (!_isFourLetterName(word)) {
            return;
        }
        break;
    case 5:
        if (!_isFiveLetterName(word)) {
            return;
        }
        break;
    case 6:
        if (!_isLongName(6, word)) {
            return;
        }
        break;
    case 7:
        if (!_isLongName(7, word)) {
            return;
        }
        break;
    case 8:
        if (!_isLongName(8, word)) {
            return;
        }
        break;
    case 9:
        if (!_isLongName(9, word)) {
            return;
        }
        break;
    case 10:
        if (!_isLongName(10, word)) {
            return;
        }
        break;
    default:
        return;
    }

    _fetchAndShowPokemon(word);
}

function is_terminator(char: string): boolean {
    return char === ' ' || char === '?' || char === '!' || char === ',' || char === ':' || char === ';' || char === '(' || char === ')';
}

/**
 * @return {void}
 */
function _handleMessage(
    message: ChatMessage): void
{
    if (message.contents?.[0] === '!') {
        return;
    }

    const end = message.contents.length;

    if (end < 3) {
        return;
    }

    const emotes: Record<string, string> = {};

    for (const key in message.emotes) {
        for (const item of message.emotes[key]) {
            emotes[item] = key;
        }
    }

    let i = 0;
    let skip = false;

    for (let j = 0; j < end; j++) {
        const code = message.contents.charCodeAt(j);

        if (!(code >= 65 && code <= 90) &&
            !(code >= 97 && code <= 122) &&
            !(code === 32 || code === 45 || code === 46))
        {
            skip = true;
        }

        if (i < j && message.contents[j] === ' ') {
            if (skip) {
                skip = false;
            } else if (!(`${i}-${j-1}` in emotes)) {
                _checkWordForPokemon(message.contents.substring(i, j));
            }
            i = j + 1;
        }
    }

    if (!skip) {
        if (!i) {
            _checkWordForPokemon(message.contents);
        } else {
            _checkWordForPokemon(message.contents.substring(i));
        }
    }
}

/**
 * Public Functions
*/

/**
 * @return {Promise<void>}
 */
export function PokemonInit(): Promise<void>
{
    return new Promise((resolve) => {
        _mask = new Pixi.Graphics;
        _mask.beginFill(0xFFFFFF);
        _mask.drawRect(0, 0, 342, 298);
        _mask.endFill();

        _container = new Pixi.Container;
        //_container.mask = _mask;

        _gfx = new Pixi.Application({
            width: window.innerWidth,
            height: window.innerHeight,
            resizeTo: window,
            antialias: false,
            backgroundAlpha: 0,
        });

        _gfx.stage.addChild(_container);

        document.body.appendChild(_gfx.view);

        fetch('/json/names.json').then(async (response) => {
            _names = await response.json();
            _update(1);
            ChatAddEventHandler(_handleMessage);
            resolve();
        });
    });
}
